function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Page, Button, Typography, TextField, MenuItem, Card, Divider, Spinner, Checkbox } from 'seagull';
import { cityNameValidation, companyNameValidation, postalCodeValidation, streetNameValidation } from 'validators';
import { Intent } from "../../infra/access-control";
import { selectors } from "../../store/state-selectors";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { useUploadLogo } from "../../hooks/useUploadLogo";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { useStartKYC } from "../../hooks/useStartKYCPersona";
import { KYBProgressStatusEnum, KYBProgressStatusHelpers } from 'anchor-kyb-service/types';
import { useConnectBankAccount } from "../../hooks/useConnectBankAccount";
import { isBankAccountConnected } from "../../store/store.model";
import { makeSentryIgnoredError } from "../../components/ErrorBoundaryPage";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import { ownershipTypes, states } from "../../constants";
import { handleRequest } from "../../infra/utils";
import { helpArticles } from "../../utils/helpArticles";
import { useStore, useStoreActions } from "../../contexts";
import styles from "./BusinessDetails.module.scss";
const formId = 'business-details-form';
const testPrefix = 'settings-business-details';
const title = 'Business details';
const ownershipTypeList = ownershipTypes.map(_ref => {
  let {
    value,
    display
  } = _ref;
  return {
    value,
    label: display
  };
});
export function BusinessDetails() {
  const worksAt = useStore(selectors.user.business);
  const workspaces = useStore(selectors.user.workspaces);
  if (!workspaces) throw makeSentryIgnoredError('no workspaces');
  if (!worksAt) throw makeSentryIgnoredError('no business');
  return /*#__PURE__*/React.createElement(BusinessDetailsWithData, {
    worksAt: worksAt,
    workspaces: workspaces
  });
}
function BusinessDetailsWithData(_ref2) {
  let {
    worksAt,
    workspaces
  } = _ref2;
  const isVendor = useStore(selectors.user.isVendor);
  const payoutBankAccount = useStore(selectors.user.payoutBankAccount);
  const invoiceLineItemsGrouped = useStore(selectors.user.invoiceLineItemsGrouped);
  const {
    uploadLogo
  } = useUploadLogo(worksAt);
  const {
    api,
    setWorksAt,
    logBI,
    setWorkspaces
  } = useStoreActions();
  const {
    Description,
    ConnectButton,
    BankAccountDetails
  } = useConnectBankAccount();
  const {
    startKYC
  } = useStartKYC();
  const personaKYBStatus = useStore(s => {
    var _s$personaKYBStatus$d;
    return (_s$personaKYBStatus$d = s.personaKYBStatus.data) === null || _s$personaKYBStatus$d === void 0 ? void 0 : _s$personaKYBStatus$d.status;
  });
  const [grouped, setGrouped] = useState(invoiceLineItemsGrouped);
  useEffect(() => {
    document.title = title;
  }, []);
  const businessOwnership = useMemo(() => ownershipTypeList.find(_ref3 => {
    let {
      value
    } = _ref3;
    return value === worksAt.ownership;
  }) || null, [worksAt.ownership]);
  const {
    control,
    formState,
    handleSubmit,
    setValue
  } = useForm({
    mode: 'all',
    defaultValues: getInitialFormState()
  });
  function getInitialFormState() {
    var _worksAt$postalAddres, _worksAt$postalAddres2, _worksAt$postalAddres3, _worksAt$postalAddres4, _worksAt$postalAddres5, _worksAt$postalAddres6;
    return {
      businessName: worksAt.name,
      address1: (_worksAt$postalAddres = worksAt.postalAddress) === null || _worksAt$postalAddres === void 0 ? void 0 : _worksAt$postalAddres.addressOne,
      address2: (_worksAt$postalAddres2 = (_worksAt$postalAddres3 = worksAt.postalAddress) === null || _worksAt$postalAddres3 === void 0 ? void 0 : _worksAt$postalAddres3.addressTwo) !== null && _worksAt$postalAddres2 !== void 0 ? _worksAt$postalAddres2 : '',
      city: (_worksAt$postalAddres4 = worksAt.postalAddress) === null || _worksAt$postalAddres4 === void 0 ? void 0 : _worksAt$postalAddres4.city,
      state: (_worksAt$postalAddres5 = worksAt.postalAddress) === null || _worksAt$postalAddres5 === void 0 ? void 0 : _worksAt$postalAddres5.state,
      postalCode: (_worksAt$postalAddres6 = worksAt.postalAddress) === null || _worksAt$postalAddres6 === void 0 ? void 0 : _worksAt$postalAddres6.postalCode,
      ownership: businessOwnership,
      logoUrl: worksAt.logoUrl
    };
  }
  async function onSubmit(_ref4) {
    var _initialFormState$own;
    let {
      businessName,
      address1,
      address2,
      city,
      state,
      postalCode,
      ownership,
      logoFile,
      logoUrl
    } = _ref4;
    logBI(10420);
    if (logoUrl && logoFile && worksAt && logoUrl !== worksAt.logoUrl) {
      await uploadLogo({
        file: logoFile,
        asDataURL: logoUrl
      }, () => setValue('logoUrl', worksAt.logoUrl));
    }
    const params = {};
    const initialFormState = getInitialFormState();
    if (businessName !== initialFormState.businessName) params.name = businessName;
    if (address1 !== initialFormState.address1 || address2 !== initialFormState.address2 || city !== initialFormState.city || state !== initialFormState.state || postalCode !== initialFormState.postalCode) {
      var _worksAt$postalAddres7;
      params.postalAddress = {
        address1,
        address2: address2 || null,
        city,
        state,
        postalCode,
        country: ((_worksAt$postalAddres7 = worksAt.postalAddress) === null || _worksAt$postalAddres7 === void 0 ? void 0 : _worksAt$postalAddres7.country) || 'US'
      };
    }
    if (ownership && ownership.value !== ((_initialFormState$own = initialFormState.ownership) === null || _initialFormState$own === void 0 ? void 0 : _initialFormState$own.value)) params.ownership = ownership.value;
    if (!Object.keys(params).length) return;
    const {
      success
    } = await handleRequest(api.vendor.updateBusinessDetails(worksAt.id, params));
    if (success) {
      var _worksAt$postalAddres8, _worksAt$postalAddres9;
      const {
        name,
        ownership,
        postalAddress
      } = params;
      const shouldChangeWorkspace = !!name;
      setWorksAt(_objectSpread(_objectSpread({}, worksAt), {}, {
        name: name !== null && name !== void 0 ? name : worksAt.name,
        ownership: ownership !== null && ownership !== void 0 ? ownership : worksAt.ownership,
        postalAddress: postalAddress ? {
          addressOne: postalAddress.address1,
          addressTwo: postalAddress.address2,
          city: postalAddress.city,
          state: postalAddress.state,
          postalCode: postalAddress.postalCode,
          country: (_worksAt$postalAddres8 = (_worksAt$postalAddres9 = worksAt.postalAddress) === null || _worksAt$postalAddres9 === void 0 ? void 0 : _worksAt$postalAddres9.country) !== null && _worksAt$postalAddres8 !== void 0 ? _worksAt$postalAddres8 : 'US'
        } : worksAt.postalAddress
      }));
      if (shouldChangeWorkspace) {
        const newWorkspaces = workspaces.map(workspace => workspace.id === worksAt.id ? _objectSpread(_objectSpread({}, workspace), {}, {
          name
        }) : workspace);
        setWorkspaces(newWorkspaces);
      }
    }
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Page, {
    header: title,
    "data-testid": testPrefix + '-screen',
    skin: "secondary"
  }, /*#__PURE__*/React.createElement("form", {
    id: formId,
    onSubmit: handleSubmit(onSubmit)
  }, /*#__PURE__*/React.createElement(Card, {
    className: styles.fieldsLayout,
    autoHeight: true,
    padding: "m",
    disableSeparator: true,
    title: "Details"
  }, /*#__PURE__*/React.createElement(Divider, {
    margin: 8
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "businessName",
    rules: companyNameValidation('Business name', {
      required: 'Please enter your business name'
    }),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "Business name",
      "data-testid": testPrefix + '-business-name'
    }))
  }), isVendor && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "address1",
    rules: streetNameValidation(undefined, {
      required: 'Please enter a street address'
    }),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "Address 1",
      "data-testid": testPrefix + '-address1'
    }))
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "address2",
    rules: streetNameValidation(),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "Address 2",
      "data-testid": testPrefix + '-address2'
    }))
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.address
  }, /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "postalCode",
    rules: postalCodeValidation(undefined, {
      required: 'Please enter a postal code'
    }),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "Postal code",
      "data-testid": testPrefix + '-postal-code'
    }))
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "city",
    rules: cityNameValidation(undefined, {
      required: 'Please enter a city'
    }),
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({}, connectControllerToTextField(p), {
      label: "City",
      "data-testid": testPrefix + '-city'
    }))
  }), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    name: "state",
    rules: {
      required: true
    },
    render: p => /*#__PURE__*/React.createElement(TextField, _extends({
      select: true,
      label: "State",
      menuMaxHeight: 300
    }, connectControllerToTextField(p), {
      "data-testid": testPrefix + '-dropdown-state'
    }), states.map(state => /*#__PURE__*/React.createElement(MenuItem, {
      key: state,
      value: state
    }, state)))
  }))), /*#__PURE__*/React.createElement(Button, {
    intent: Intent.UpdateBusinessDetails,
    type: "submit",
    form: formId,
    disabled: !formState.isValid,
    loading: formState.isSubmitting,
    "data-testid": testPrefix + '-btn-save-changes'
  }, "Save changes")), personaKYBStatus && isVendor && /*#__PURE__*/React.createElement(Card, {
    className: styles.fieldsLayout,
    autoHeight: true,
    disableSeparator: true,
    padding: "m",
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "h5",
      weight: "bold",
      "data-testid": "tax-information-title"
    }, "Tax information"), /*#__PURE__*/React.createElement(Typography, {
      variant: "paragraph1"
    }, "This information is used by Anchor to issue your 1099-K. ", /*#__PURE__*/React.createElement(ExternalLinkButton, {
      href: helpArticles.kFormIssuance2024(),
      testid: 'tax-information-read-more',
      underline: "none",
      openInNewTab: true
    }, "Learn more")))
  }, /*#__PURE__*/React.createElement(Divider, {
    margin: 0
  }), [KYBProgressStatusEnum.NotStarted, KYBProgressStatusEnum.DetailsNeeded].includes(personaKYBStatus) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, null, "Tax information becomes available after successfully completing the KYB verification."), /*#__PURE__*/React.createElement(Button, {
    onClick: startKYC,
    "data-testid": "complete-kyb-button"
  }, "Complete KYB")), (KYBProgressStatusHelpers.isAwaitingDecision(personaKYBStatus) || personaKYBStatus === KYBProgressStatusEnum.Declined) && /*#__PURE__*/React.createElement("div", {
    "data-testid": "awaiting-kyb"
  }, /*#__PURE__*/React.createElement(Typography, null, "Tax information is available after successfully completing the KYB verification."), /*#__PURE__*/React.createElement(Typography, {
    skin: "disabled"
  }, "*Verification process takes several minutes up to three business days.")), personaKYBStatus === KYBProgressStatusEnum.Approved && /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: () => api.vendor.getTaxInfo(worksAt.id),
    renderLoader: () => /*#__PURE__*/React.createElement(Spinner, {
      centeredInBox: true,
      centerBoxWidth: "100%",
      "data-testid": "tax-info-loader"
    }),
    renderChild: _ref5 => {
      let {
        data
      } = _ref5;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextField, {
        label: "Business legal name",
        value: data.businessLegalName,
        disabled: true,
        "data-testid": "business-legal-name"
      }), /*#__PURE__*/React.createElement(TextField, {
        label: "Business registered address",
        value: data.registeredBusinessAddress,
        disabled: true,
        "data-testid": "registered-business-address"
      }), /*#__PURE__*/React.createElement("div", {
        className: styles.taxIdContainer
      }, /*#__PURE__*/React.createElement(TextField, {
        style: {
          flex: '3'
        },
        label: "Business Tax Identification Number",
        value: data.businessTaxId,
        disabled: true,
        "data-testid": "business-tax-id"
      }), /*#__PURE__*/React.createElement(TextField, {
        style: {
          flex: '1'
        },
        label: "Type",
        value: data.businessTaxIdType.toUpperCase(),
        disabled: true,
        "data-testid": "business-tax-id-type"
      })), /*#__PURE__*/React.createElement(TextField, {
        label: "Merchant Category Code",
        value: data.merchantCategoryCode,
        disabled: true,
        "data-testid": "merchant-category-code"
      }));
    }
  })), isVendor && /*#__PURE__*/React.createElement(Card, {
    className: styles.fieldsLayout,
    autoHeight: true,
    padding: "m",
    disableSeparator: true,
    title: 'Billing settings'
  }, /*#__PURE__*/React.createElement(Divider, {
    margin: 0
  }), /*#__PURE__*/React.createElement(Checkbox, {
    checked: grouped,
    onChange: e => {
      setGrouped(e.currentTarget.checked);
    },
    "data-testid": "group-invoice-line-items"
  }, "Group identical services into a single line item on invoices"), /*#__PURE__*/React.createElement(Button, {
    "data-testid": "save-billing-changes",
    onClick: async () => {
      var _worksAt$settings;
      if (grouped === invoiceLineItemsGrouped || !worksAt.settings) return;
      const invoiceLineItems = grouped ? 'grouped' : 'detailed';
      const {
        success
      } = await handleRequest(api.vendor.updateBusinessSettings(worksAt.id, _objectSpread(_objectSpread({}, worksAt.settings), {}, {
        invoiceLineItems,
        coverCreditCardFees: ((_worksAt$settings = worksAt.settings) === null || _worksAt$settings === void 0 ? void 0 : _worksAt$settings.feeControls.coverCreditCardFees) || false
      })));
      if (success) {
        setWorksAt(_objectSpread(_objectSpread({}, worksAt), {}, {
          settings: worksAt.settings && _objectSpread(_objectSpread({}, worksAt.settings), {}, {
            invoiceLineItems
          })
        }));
      }
    }
  }, "Save changes")), isVendor && /*#__PURE__*/React.createElement(Card, {
    className: styles.fieldsLayout,
    autoHeight: true,
    padding: "m",
    disableSeparator: true,
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
      weight: "normal",
      variant: "h5"
    }, "Bank account information"), /*#__PURE__*/React.createElement(Description, {
      testid: "bank-account-details-title",
      linkTestid: "bank-account-details-read-more"
    })),
    "data-testid": 'bank-account-details'
  }, /*#__PURE__*/React.createElement(Divider, {
    margin: 0
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.bankDetails
  }, isBankAccountConnected(payoutBankAccount) ? /*#__PURE__*/React.createElement(BankAccountDetails, {
    payoutBankAccount: payoutBankAccount
  }) : /*#__PURE__*/React.createElement(Typography, null, "No bank account connected."), /*#__PURE__*/React.createElement(ConnectButton, {
    className: styles.bankActions,
    enterManuallyTestid: "enter-manually",
    testid: "connect-bank-account"
  }))))));
}